import { z } from "zod";
import { http } from "@/shared/api/axios";

export const LoginPayload = z.object({
    email: z.string(),
    password: z.string(),
});

export const AvailableAccountSchema = z.object({
    accountId: z.number(),
    accountName: z.string(),
    domain: z.string(),
    employeeType: z.string(),
    logoFileId: z.string().nullable(),
    setupState: z.enum(["Provisioning", "Ready", "Failed"]).default("Ready"),
    accessible: z.boolean().optional(),
    accessState: z.enum(["Active", "Ended", "Suspended", "Withdrawn"]).optional().catch(undefined),
});

export const LoginResponse = z.object({
    userId: z.number(),
    accessToken: z.string(),
    refreshToken: z.string(),
    accessTokenExpiresAt: z.string(),
    refreshTokenExpiresAt: z.string(),
    availableAccounts: z.array(AvailableAccountSchema),
});

export const RefreshTokenResponse = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    accessTokenExpiresAt: z.string(),
    refreshTokenExpiresAt: z.string(),
});

type LoginReqSchema = z.infer<typeof LoginPayload>;

export async function login(data: LoginReqSchema, signal?: AbortSignal) {
    const res = await http.post("/v1/auth/login", data, { signal });
    return LoginResponse.parse(res.data);
}

export async function refreshAccessToken(refreshToken: string) {
    const res = await http.post("/v1/token/refresh", { refreshToken });
    return RefreshTokenResponse.parse(res.data);
}

export async function logout(refreshToken: string) {
    await http.post("/v1/token/revoke", { refreshToken });
}

export const FetchPermissionsResponse = z.object({
    permissions: z.array(z.string())
});

export async function fetchMyPermissions() {
    const res = await http.get("/v1/users/me/permissions");
    const data = FetchPermissionsResponse.parse(res.data);
    return data.permissions;
}

export async function forgotPassword(email: string) {
    await http.post("/v1/auth/forgot-password", { email });
}

export async function resetPassword(token: string, newPassword: string) {
    await http.post("/v1/auth/reset-password", { token, newPassword });
}

export const SignupRequest = z.object({
    email: z.string(),
    password: z.string(),
    firstName: z.string(),
    lastName: z.string(),
});

export type SignupRequestType = z.infer<typeof SignupRequest>;

export async function signup(data: SignupRequestType) {
    await http.post("/v1/auth/signup", data);
}

export const VerifyEmailResponse = z.object({
    login: LoginResponse.optional(),
    signInRequired: z.boolean().optional(),
});

export async function verifyEmail(token: string, password: string) {
    const res = await http.post("/v1/auth/verify-email", { token, password });
    return VerifyEmailResponse.parse(res.data);
}

export async function resendVerification(email: string) {
    await http.post("/v1/auth/resend-verification", { email });
}

export const LOGIN_USER_NOT_VERIFIED = "USER_NOT_VERIFIED";

export function isUserNotVerifiedError(data: unknown): boolean {
    if (!data || typeof data !== "object") return false;
    const errorCode = (data as { errorCode?: unknown }).errorCode;
    return errorCode === LOGIN_USER_NOT_VERIFIED;
}
